/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../../utils/axiosInstance';
import {
  Title,
  Button,
  Input,
  FlexBox,
  FlexBoxDirection,
  FlexBoxJustifyContent,
  FlexBoxAlignItems,
  DatePicker,
  BusyIndicator,
  Select,
  Option,
  AnalyticalTable,
  MessageStrip,
  Label,
  Icon,
  Bar,
  Text,
  ObjectStatus
} from '@ui5/webcomponents-react';
import Layout from '../Layout';
import { getStatusStyle, getStatusComponentProps } from '../../utils/validator';
import useAutoTranslate from '../../hooks/useAutoTranslate';

export const ScheduleList = () => {
  const location = useLocation();
  const isRenderedInHome = location.pathname === '/home';
  const navigate = useNavigate();
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  
  // Búsqueda avanzada
  const [searchParams, setSearchParams] = useState({
    orderNumber: '',
    appoimentNumber: '',
    status: '',
    startDate: null,
    endDate: null
  });

  const [shouldSearch, setShouldSearch] = useState(false);
  const [isAdvancedSearchActive, setIsAdvancedSearchActive] = useState(false);

  // Hook de traducción
  const { autoT } = useAutoTranslate();

  const statusOptions = [
    { value: '', label: autoT('Todos los estados') },
    { value: 'PENDING', label: autoT('Pendiente') },
    { value: 'COMPLETED', label: autoT('Completado') },
    { value: 'CANCELLED', label: autoT('Cancelado') },
    { value: 'DELAYED', label: autoT('Atrasado') }
  ];

  const getStatusLabel = (status) => {
    const option = statusOptions.find(opt => opt.value === status);
    return option ? option.label : status;
  };

  const columns = [
    {
      Header: autoT('N° Agendamiento'),
      accessor: 'appoimentNumber',
      width: 150
    },
    {
      Header: autoT('N° Orden'),
      accessor: 'orderNumber',
      width: 150,
      Cell: ({ value, row }) => (
        <Button
          design="Transparent"
          onClick={() => navigate(`/order-detail/${value}`)}
        >
          {value}
        </Button>
      )
    },
    {
      Header: autoT('Tipo'),
      accessor: 'isDelivery',
      width: 120,
      Cell: ({ value }) => value === 'DELIVERY' ? autoT('Despacho') : autoT('Retiro')
    },
    {
      Header: autoT('Fecha Agendada'),
      accessor: 'scheduleDate',
      width: 180,
      Cell: ({ value }) => new Date(value).toLocaleString('es-CL')
    },
    {
      Header: autoT('Días Restantes'),
      accessor: 'daysRemaining',
      width: 150,
      Cell: ({ row }) => {
        const today = new Date();
        const scheduleDate = new Date(row.original.scheduleDate);
        const diffTime = scheduleDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        let statusToUse = 'NEUTRAL'; // Estado por defecto
        
        if (diffDays < 0) {
          statusToUse = 'DELAYED'; // Atrasado
        } else if (diffDays === 0) {
          statusToUse = 'WARNING'; // Hoy
        }
        
        const style = getStatusStyle(statusToUse);
        
        return (
          <Text
            style={{
              color: style.color,
              fontWeight: diffDays < 0 ? 'bold' : 'normal'
            }}
          >
            {diffDays === 0 
              ? autoT('Hoy') 
              : diffDays < 0 
                ? `${autoT('Atrasada por')} ${Math.abs(diffDays)} ${autoT('días')}`
                : `${diffDays} ${autoT('días')}`
            }
          </Text>
        );
      }
    },
    {
      Header: autoT('Contacto'),
      accessor: 'contactName',
      width: 125
    },
    {
      Header: autoT('Teléfono'),
      accessor: 'contactPhone',
      width: 150
    },
    {
      Header: autoT('Email'),
      accessor: 'contactEmail',
      width: 200
    },
    {
      Header: autoT('Dirección'),
      accessor: 'scheduleAdress'
    },
    {
      Header: autoT('N. de notificación'),
      accessor: 'asnNumber',
      width: 120,
    },
    {
      Header: autoT('Estado'),
      accessor: 'status',
      width: 150,
      Cell: ({ value, row }) => {
        const today = new Date();
        const scheduleDate = new Date(row.original.scheduleDate);
        const diffTime = scheduleDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        let statusToShow = value;
        let label = '';

        if (value === 'CANCELLED') {
          statusToShow = 'CANCELLED';
          label = autoT('Cancelado');
        } else if (diffDays < 0) {
          statusToShow = 'DELAYED';
          label = autoT('Atrasado');
        } else if (value === 'PENDING') {
          statusToShow = 'PENDING';
          label = autoT('Pendiente');
        } else {
          label = getStatusLabel(value);
        }

        return (
          <ObjectStatus
            style={getStatusComponentProps(statusToShow, null, { width: '120px' }).style}
          >
            {label}
          </ObjectStatus>
        );
      }
    }
  ];

  const fetchSchedules = async (page = currentPage) => {
    setLoading(true);
    try {
      if (shouldSearch) {
        const debounceTimeout = setTimeout(() => {
          handleAdvancedSearch();
          setShouldSearch(false);
        }, 500);
        
        return () => clearTimeout(debounceTimeout);
      }

      console.log(autoT('Usando endpoint general: purchase-order-schedules'));
      const response = await axiosInstance.get('purchase-order-schedules', {
        params: {
          page,
          size: pageSize,
          sortBy: 'scheduleDate',
          direction: 'desc'
        }
      });
      
      if (response.data.data?.data) {
        setSchedules(response.data.data.data);
        setTotalPages(Math.ceil(response.data.data.totalCount / pageSize));
      } else if (Array.isArray(response.data)) {
        setSchedules(response.data);
        setTotalPages(Math.ceil(response.data.length / pageSize));
      }
      
      setError(null);
    } catch (err) {
      console.error(autoT('Error fetching schedules:'), err);
      setError(autoT('Error al cargar los agendamientos'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchedules();
  }, [currentPage]);

  useEffect(() => {
    if (shouldSearch) {
      const debounceTimeout = setTimeout(() => {
        handleAdvancedSearch();
        setShouldSearch(false);
      }, 500);
      
      return () => clearTimeout(debounceTimeout);
    }
  }, [shouldSearch]);

  const handleSearchInputChange = (field, value) => {
    setSearchParams(prev => ({
      ...prev,
      [field]: typeof value === 'string' ? value.trim() : value
    }));
    
    if (field !== 'startDate' && field !== 'endDate') {
      setShouldSearch(true);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return '';
      
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error(autoT("Error al formatear fecha:"), error);
      return '';
    }
  };

  const handleStartDateChange = (event) => {
    try {
      const dateValue = event.detail.value;
      if (!dateValue) {
        setSearchParams(prev => ({
          ...prev,
          startDate: null
        }));
        setError('');
        return;
      }
      
      let newStartDate;
      try {
        const [day, month, year] = dateValue.split('/');
        if (day && month && year) {
          newStartDate = new Date(`${year}-${month}-${day}T00:00:00`);
        } else {
          newStartDate = new Date(dateValue);
        }
      } catch (error) {
        newStartDate = new Date(dateValue);
      }
      
      if (isNaN(newStartDate.getTime())) {
        console.error(autoT("Fecha de inicio inválida:"), dateValue);
        setError(autoT('Fecha de inicio inválida'));
        return;
      }
      
      if (searchParams.endDate) {
        const endDate = new Date(searchParams.endDate);
        if (newStartDate > endDate) {
          setError(autoT('La fecha de inicio no puede ser posterior a la fecha de fin'));
          return;
        }
      }
      
      setError('');
      setSearchParams(prev => ({
        ...prev,
        startDate: newStartDate.toISOString()
      }));
      
      if (searchParams.endDate) {
        setShouldSearch(true);
      }
    } catch (error) {
      console.error(autoT("Error al procesar fecha de inicio:"), error);
      setError(autoT('Error al procesar la fecha de inicio'));
    }
  };

  const handleEndDateChange = (event) => {
    try {
      const dateValue = event.detail.value;
      if (!dateValue) {
        setSearchParams(prev => ({
          ...prev,
          endDate: null
        }));
        setError('');
        return;
      }
      
      let newEndDate;
      try {
        const [day, month, year] = dateValue.split('/');
        if (day && month && year) {
          newEndDate = new Date(`${year}-${month}-${day}T23:59:59`);
        } else {
          newEndDate = new Date(dateValue);
        }
      } catch (error) {
        newEndDate = new Date(dateValue);
      }
      
      if (isNaN(newEndDate.getTime())) {
        console.error(autoT("Fecha de fin inválida:"), dateValue);
        setError(autoT('Fecha de fin inválida'));
        return;
      }
      
      if (searchParams.startDate) {
        const startDate = new Date(searchParams.startDate);
        if (newEndDate < startDate) {
          setError(autoT('La fecha de fin no puede ser anterior a la fecha de inicio'));
          return;
        }
      }
      
      setError('');
      setSearchParams(prev => ({
        ...prev,
        endDate: newEndDate.toISOString()
      }));
      
      if (searchParams.startDate) {
        setShouldSearch(true);
      }
    } catch (error) {
      console.error(autoT("Error al procesar fecha de fin:"), error);
      setError(autoT('Error al procesar la fecha de fin'));
    }
  };

  const handleAdvancedSearch = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      if (searchParams.orderNumber) {
        params.append('orderNumber', searchParams.orderNumber.trim());
      }
      
      if (searchParams.appoimentNumber) {
        params.append('appoimentNumber', searchParams.appoimentNumber.trim());
      }
      
      if (searchParams.status) {
        params.append('status', searchParams.status);
      }
      
      if (searchParams.startDate && searchParams.endDate) {
        params.append('startDate', searchParams.startDate);
        params.append('endDate', searchParams.endDate);
      }

      params.append('page', currentPage);
      params.append('size', pageSize);

      const hasSearchParams = params.toString().length > 0;
      setIsAdvancedSearchActive(hasSearchParams);

      if (hasSearchParams) {
        console.log(autoT('URL de búsqueda: purchase-order-schedules/search'));
        console.log(autoT('Parámetros:'), Object.fromEntries(params));

        const response = await axiosInstance.get('purchase-order-schedules/search', {
          params: Object.fromEntries(params)
        });
        
        if (response.data.data?.data) {
          setSchedules(response.data.data.data);
          setTotalPages(Math.ceil(response.data.data.totalCount / pageSize));
        } else if (Array.isArray(response.data)) {
          setSchedules(response.data);
          setTotalPages(Math.ceil(response.data.length / pageSize));
        }
      } else {
        fetchSchedules(currentPage);
      }
      
      setError(null);
    } catch (err) {
      console.error(autoT('Error en búsqueda avanzada:'), err);
      setError(autoT('Error al realizar la búsqueda avanzada'));
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    console.log(autoT('Limpiando búsqueda'));
    setSearchParams({
      orderNumber: '',
      appoimentNumber: '',
      status: '',
      startDate: null,
      endDate: null
    });
    setError('');
    setIsAdvancedSearchActive(false);
    setCurrentPage(0);
    fetchSchedules(0);
  };

  const getRowStyle = (row) => {
    const today = new Date();
    const scheduleDate = new Date(row.original.scheduleDate);
    const diffTime = scheduleDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0 && row.original.status !== 'CANCELLED') {
      return {
        backgroundColor: getStatusStyle('DELAYED').backgroundColor,
        borderLeft: '4px solid ' + getStatusStyle('DELAYED').color
      };
    }
    
    if (row.original.status === 'PENDING') {
      return {
        backgroundColor: getStatusStyle('PENDING').backgroundColor,
        borderLeft: '4px solid ' + getStatusStyle('PENDING').color
      };
    }
    
    return {};
  };

  const content = (
    <div style={{ padding: '1rem', position: 'relative', paddingBottom: '80px' }}>
      <FlexBox
        justifyContent={FlexBoxJustifyContent.SpaceBetween}
        style={{ marginBottom: '1rem' }}
      >
        <Title>{autoT('Agendamientos')}</Title>
      </FlexBox>

      {/* Búsqueda Avanzada */}
      <div style={{ 
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '1rem',
        padding: '1.5rem',
        position: 'relative'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '1rem',
          marginBottom: '1rem'
        }}>
          <div>
            <Label htmlFor="orderNumber" style={{ display: 'block', marginBottom: '0.5rem' }}>
              {autoT('Número de orden')}
            </Label>
            <Input
              id="orderNumber"
              value={searchParams.orderNumber}
              onChange={(e) => handleSearchInputChange('orderNumber', e.target.value)}
              placeholder={autoT('Ingrese número de orden')}
              style={{ width: '100%' }}
            />
          </div>

          <div>
            <Label htmlFor="appoimentNumber" style={{ display: 'block', marginBottom: '0.5rem' }}>
              {autoT('Número de agendamiento')}
            </Label>
            <Input
              id="appoimentNumber"
              value={searchParams.appoimentNumber}
              onChange={(e) => handleSearchInputChange('appoimentNumber', e.target.value)}
              placeholder={autoT('Ingrese número de agendamiento')}
              style={{ width: '100%' }}
            />
          </div>

          <div>
            <Label htmlFor="status" style={{ display: 'block', marginBottom: '0.5rem' }}>
              {autoT('Estado')}
            </Label>
            <Select
              id="status"
              value={searchParams.status}
              onChange={(e) => handleSearchInputChange('status', e.detail.selectedOption.value)}
              style={{ width: '100%' }}
            >
              <Option value="">{autoT('Todos')}</Option>
              {statusOptions.map((status) => (
                <Option key={status.value} value={status.value}>
                  {status.label}
                </Option>
              ))}
            </Select>
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr auto',
          gap: '1rem',
          alignItems: 'flex-end'
        }}>
          <div>
            <Label htmlFor="startDate" style={{ display: 'block', marginBottom: '0.5rem' }}>
              {autoT('Fecha Inicio')}
            </Label>
            <DatePicker
              id="startDate"
              onChange={handleStartDateChange}
              value={searchParams.startDate ? formatDate(searchParams.startDate) : ''}
              style={{ width: '100%' }}
              formatPattern="dd/MM/yyyy"
            />
          </div>

          <div>
            <Label htmlFor="endDate" style={{ display: 'block', marginBottom: '0.5rem' }}>
              {autoT('Fecha Fin')}
            </Label>
            <DatePicker
              id="endDate"
              onChange={handleEndDateChange}
              value={searchParams.endDate ? formatDate(searchParams.endDate) : ''}
              style={{ width: '100%' }}
              formatPattern="dd/MM/yyyy"
            />
          </div>

          <Button 
            design="Transparent"
            onClick={clearSearch}
            style={{ color: '#0854A0' }}
          >
            {autoT('Limpiar Filtros')}
          </Button>
        </div>
      </div>

      {loading ? (
        <BusyIndicator active />
      ) : (
        <>
          <AnalyticalTable
            columns={columns}
            data={schedules}
            header={autoT('Agendamientos')}
            selectedRowIds={{}}
            selectionMode="None"
            withRowHighlight
            minRows={10}
            rowHeight={44}
            alternateRowColor
            scaleWidthMode="Smart"
            numberOfSelectedRows={pageSize}
            visibleRows={pageSize}
            onRowsUpdate={(e) => setCurrentPage(e.detail.firstVisibleRow / pageSize)}
            rowStyle={getRowStyle}
          />
          
          <div style={{ 
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: 'white',
            padding: '1rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: '0 -2px 4px rgba(0,0,0,0.1)',
            borderTop: '1px solid #e5e5e5',
            zIndex: 100
          }}>
            <div style={{ width: '100%', maxWidth: '98%', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))} disabled={currentPage === 0}>
                ← {autoT('Anterior')}
              </Button>
              <span style={{ flex: 1, textAlign: 'center' }}>
                {autoT('Página')} {currentPage + 1}
              </span>
              <Button onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}>
                {autoT('Siguiente')} →
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );

  if (loading) {
    const loadingContent = (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <BusyIndicator active size="Large" />
      </div>
    );
    return isRenderedInHome ? loadingContent : <Layout>{loadingContent}</Layout>;
  }

  if (error) {
    const errorContent = <div>{autoT('Error:')} {error}</div>;
    return isRenderedInHome ? errorContent : <Layout>{errorContent}</Layout>;
  }

  return isRenderedInHome ? content : <Layout>{content}</Layout>;
};

export default ScheduleList;